import type { BoosterKind } from '../catalog/boosters';
import { getModifierForGiant } from '../catalog/giantLevelModifiers';
import type { GiantId } from '../ids/ids';
import type { GiantItemsLayout } from '../level/itemsLayout';
import {
  RUNNER_COLLECTIBLE_SPRITE_HEIGHT_SVG,
  RUNNER_COLLECTIBLE_SPRITE_WIDTH_S,
  RUNNER_MODIFIER_ZONE_SVG_WIDTH,
  RUNNER_MODIFIER_ZONE_SVG_X,
  RUNNER_REF_VIEWBOX_WIDTH,
  RUNNER_SHIELD_BOOSTER_SPRITE_HEIGHT_SVG,
  RUNNER_SHIELD_BOOSTER_SPRITE_WIDTH_S,
  RUNNER_SPEED_BOOSTER_SPRITE_HEIGHT_SVG,
  RUNNER_SPEED_BOOSTER_SPRITE_WIDTH_S,
  RUNNER_STATIC_OBSTACLE_SIZE_SCALE,
  RUNNER_SERJ_STATIC_OBSTACLE_VISUAL_SCALE,
  RUNNER_YETTI_STATIC_OBSTACLE_VISUAL_SCALE,
  RUNNER_MARKER_SIZE_S,
  runnerSpawnSvgXOffset,
} from './runnerConstants';
import type { RunnerBoosterPickup, RunnerHitbox, RunnerLevel, RunnerObstacle, RunnerSpark } from './runnerTypes';
import { factorStaticSpinDurationSec } from './factorMiragePaths';
import { svamiStaticWobbleDurationSec } from './svamiObstaclePaths';
import { buildMonaMovingObstacle } from './monaMovingPaths';

/** Giants whose static obstacle sprite is drawn larger than the collision AABB. */
const STATIC_OBSTACLE_VISUAL_SCALE_BY_GIANT: Partial<Record<GiantId, number>> = {
  serj: RUNNER_SERJ_STATIC_OBSTACLE_VISUAL_SCALE,
  yetti: RUNNER_YETTI_STATIC_OBSTACLE_VISUAL_SCALE,
};

function spriteScaleS(viewBoxWidth: number): number {
  return viewBoxWidth / RUNNER_REF_VIEWBOX_WIDTH;
}

function hitboxFromPlacement(
  s: number,
  lane: number,
  widthS: number,
  viewBoxHeight: number,
  heightSvg: number,
): RunnerHitbox {
  return {
    centerS: s,
    centerLane: lane,
    widthS,
    heightLane: heightSvg / viewBoxHeight,
  };
}

function staticObstacleHitbox(
  s: number,
  lane: number,
  viewBoxWidth: number,
  viewBoxHeight: number,
): RunnerHitbox {
  const scaleS = spriteScaleS(viewBoxWidth);
  const widthS =
    RUNNER_MARKER_SIZE_S * RUNNER_STATIC_OBSTACLE_SIZE_SCALE * scaleS;
  const heightSvg =
    RUNNER_MARKER_SIZE_S * RUNNER_STATIC_OBSTACLE_SIZE_SCALE * scaleS;
  return hitboxFromPlacement(s, lane, widthS, viewBoxHeight, heightSvg);
}

function sparkHitbox(
  s: number,
  lane: number,
  viewBoxWidth: number,
  viewBoxHeight: number,
): RunnerHitbox {
  const scaleS = spriteScaleS(viewBoxWidth);
  return hitboxFromPlacement(
    s,
    lane,
    RUNNER_COLLECTIBLE_SPRITE_WIDTH_S * scaleS,
    viewBoxHeight,
    RUNNER_COLLECTIBLE_SPRITE_HEIGHT_SVG * scaleS,
  );
}

function boosterHitbox(
  kind: BoosterKind,
  s: number,
  lane: number,
  viewBoxWidth: number,
  viewBoxHeight: number,
): RunnerHitbox {
  const scaleS = spriteScaleS(viewBoxWidth);
  if (kind === 'shield') {
    return hitboxFromPlacement(
      s,
      lane,
      RUNNER_SHIELD_BOOSTER_SPRITE_WIDTH_S * scaleS,
      viewBoxHeight,
      RUNNER_SHIELD_BOOSTER_SPRITE_HEIGHT_SVG * scaleS,
    );
  }
  if (kind === 'speed') {
    return hitboxFromPlacement(
      s,
      lane,
      RUNNER_SPEED_BOOSTER_SPRITE_WIDTH_S * scaleS,
      viewBoxHeight,
      RUNNER_SPEED_BOOSTER_SPRITE_HEIGHT_SVG * scaleS,
    );
  }
  const fallback = RUNNER_MARKER_SIZE_S * scaleS;
  return hitboxFromPlacement(s, lane, fallback, viewBoxHeight, fallback);
}

export function buildRunnerLevelFromLayout(layout: GiantItemsLayout): RunnerLevel {
  const { route } = layout;
  const viewBox = { width: route.viewBoxWidth, height: route.viewBoxHeight };
  const spawnSvgX = route.spawnSvgX - runnerSpawnSvgXOffset(route.viewBoxHeight);

  const obstacles: RunnerObstacle[] = [
    ...layout.staticObstacles.map((item, index) => {
      const hitbox = staticObstacleHitbox(
        item.s,
        item.lane,
        viewBox.width,
        viewBox.height,
      );
      const visualScale = STATIC_OBSTACLE_VISUAL_SCALE_BY_GIANT[layout.giantId];
      const oversizedVisual =
        visualScale != null
          ? {
              visualWidthS: hitbox.widthS * visualScale,
              visualHeightLane: hitbox.heightLane * visualScale,
            }
          : undefined;
      return {
        id: item.id,
        anchorS: item.s,
        hitbox,
        consumed: false,
        ...oversizedVisual,
        wobbleDurationSec:
          layout.giantId === 'svami'
            ? svamiStaticWobbleDurationSec(index)
            : undefined,
        spinDurationSec:
          layout.giantId === 'factor'
            ? factorStaticSpinDurationSec(index)
            : undefined,
      };
    }),
    ...layout.movingObstacles.map((item) => buildMonaMovingObstacle(item, viewBox)),
  ];

  const sparks: RunnerSpark[] = layout.sparks.map((item) => ({
    id: item.id,
    anchorS: item.s,
    hitbox: sparkHitbox(item.s, item.lane, viewBox.width, viewBox.height),
    consumed: false,
  }));

  const boosters: RunnerBoosterPickup[] = layout.boosters.map((item) => ({
    id: item.id,
    kind: item.type,
    anchorS: item.s,
    hitbox: boosterHitbox(item.type, item.s, item.lane, viewBox.width, viewBox.height),
    consumed: false,
  }));

  const fromS = RUNNER_MODIFIER_ZONE_SVG_X - route.startPointSvgX;
  const toS = fromS + RUNNER_MODIFIER_ZONE_SVG_WIDTH;

  const levelWithoutDecors: RunnerLevel = {
    giantId: layout.giantId,
    viewBox,
    startPointSvgX: route.startPointSvgX,
    spawnSvgX,
    spawnSvgY: route.spawnSvgY,
    startLane: route.startLane,
    routeEndS: route.routeEndS,
    finishCourierPlaceSvgX: route.finishCourierPlaceSvgX,
    finishCourierPlaceSvgY: route.finishCourierPlaceSvgY,
    laneMin: route.laneMin,
    laneMax: route.laneMax,
    obstacles,
    sparks,
    boosters,
    decors: [],
    modifierZone: {
      kind: getModifierForGiant(layout.giantId),
      fromS,
      toS,
      entered: false,
    },
  };

  return levelWithoutDecors;
}
